<template>
  <div class="material-library-page">
    <div class="material-library-container">
      <div class="title-bg">物料库</div>
      <!-- 搜索区域 -->
      <v-card class="search-card">
        <v-card-text>
          <v-row class="align-center">
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="searchForm.keyword"
                label="关键字搜索"
                persistent-placeholder
                prepend-inner-icon="mdi-magnify"
                variant="outlined"
                hide-details
                density="compact"
                clearable
                class="search-input"
                placeholder="物料名称/规格/大类/小类/编码/备注"
                @keyup.enter="handleSearch"
                @click:clear="handleSearch"
              />
            </v-col>
            <v-col cols="12" sm="5">
              <div class="d-flex gap-2">
                <v-btn
                  color="primary"
                  variant="flat"
                  @click="handleSearch"
                  class="search-btn"
                  density="comfortable"
                >
                  查询
                </v-btn>
                <v-btn
                  color="primary"
                  variant="flat"
                  @click="handleReset"
                  class="search-btn"
                  density="comfortable"
                >
                  重置
                </v-btn>
              </div>
            </v-col>
            <v-col cols="12" sm="3" v-if="userStore.userInfo?.auths.includes('jf_goods')">
              <div class="d-flex justify-end">
                <v-btn
                  color="primary"
                  variant="flat"
                  @click="handleAdd"
                  class="search-btn"
                  density="comfortable"
                >
                  新增物料
                </v-btn>
                <v-btn
                  color="primary"
                  variant="flat"
                  class="ml-4 search-btn"
                  @click="importMaterial"
                  density="comfortable"
                >
                  导入物料
                </v-btn>
                <v-btn
                  color="primary"
                  variant="flat"
                  class="ml-4 search-btn"
                  @click="downloadMaterial"
                  density="comfortable"
                >
                  下载物料模板
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- 表格区域 -->
      <v-card class="table-card">
        <v-card-text class="pa-0">
          <v-table class="material-table" hover>
            <thead>
              <tr>
                <th v-for="header in headers" :key="header.key" :class="['text-' + header.align]" :style="{ width: header.width }">
                  {{ header.title }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading" class="text-center">
                <td :colspan="headers.length" class="py-8">
                  <v-progress-circular indeterminate color="primary"></v-progress-circular>
                  <div class="mt-2">加载中...</div>
                </td>
              </tr>
              <tr v-else-if="materialList.length === 0" class="text-center">
                <td :colspan="headers.length" class="py-8">暂无数据</td>
              </tr>
              <template v-else>
                <tr v-for="(item, index) in materialList" :key="item.id || index">
                  <!-- 编码 -->
                  <td class="text-center">
                    {{ item.materialCode }}
                  </td>
                  <!-- 物料名称 -->
                  <td class="text-center">
                    <v-textarea
                      v-if="userStore.userInfo?.auths.includes('jf_goods')"
                      v-model="item.materialName"
                      variant="outlined"
                      density="compact"
                      hide-details
                      rows="1"
                      auto-grow
                      resize
                      @input="debouncedSave(item, index)"
                    ></v-textarea>
                    <template v-else>{{ item.materialName }}</template>
                  </td>
                  <!-- 规格 -->
                  <td class="text-center">
                    <v-textarea
                      v-if="userStore.userInfo?.auths.includes('jf_goods')"
                      v-model="item.specDesc"
                      variant="outlined"
                      density="compact"
                      hide-details
                      rows="1"
                      auto-grow
                      resize
                      @input="debouncedSave(item, index)"
                    ></v-textarea>
                    <template v-else>{{ item.specDesc }}</template>
                  </td>
                  <!-- 备注 -->
                  <td class="text-center">
                    <v-textarea
                      v-if="userStore.userInfo?.auths.includes('jf_goods')"
                      v-model="item.materialRemark"
                      variant="outlined"
                      density="compact"
                      hide-details
                      rows="1"
                      auto-grow
                      resize
                      @input="debouncedSave(item, index)"
                    ></v-textarea>
                    <template v-else>{{ item.materialRemark || '/' }}</template>
                  </td>
                  <!-- 单位 -->
                  <td class="text-center">
                    <v-text-field
                      v-if="userStore.userInfo?.auths.includes('jf_goods')"
                      v-model="item.materialUnit"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @input="debouncedSave(item, index)"
                    ></v-text-field>
                    <template v-else>{{ item.materialUnit || '/' }}</template>
                  </td>
                  <!-- 大类 -->
                  <td class="text-center">
                    <v-select
                      v-if="userStore.userInfo?.auths.includes('jf_goods')"
                      v-model="item.bigClass"
                      :items="bigClassOptions"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="handleChangeBigClass(item, index)"
                    ></v-select>
                    <template v-else>{{ item.bigClass || '/' }}</template>
                  </td>
                  <!-- 小类 -->
                  <td class="text-center">
                    <v-select
                      v-if="userStore.userInfo?.auths.includes('jf_goods')"
                      v-model="item.materialType"
                      :items="item.bigClass ? getMaterialTypeOptions(item.bigClass) : []"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="compact"
                      hide-details
                      @update:model-value="debouncedSave(item, index)"
                    ></v-select>
                    <template v-else>{{ item.materialType || '/' }}</template>
                  </td>
                  <!-- 操作 -->
                  <td class="text-center">
                    <!-- v-if="userStore.userInfo?.auths.includes('jf_goods')" -->
                    <v-btn
                      color="error"
                      variant="tonal"
                      size="small"
                      @click="handleDelete(item, index)"
                    >
                      删除
                    </v-btn>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-table>

          <!-- 分页区域 -->
          <!-- <div class="custom-pagination d-flex align-center justify-space-between">
            <div class="pagination-info">共 {{ total }} 条记录</div>
            
            <div class="d-flex align-center">
              <div class="pagination-controls">
                <v-btn
                  icon="mdi-chevron-double-left"
                  variant="text"
                  density="comfortable"
                  :disabled="currentPage === 1"
                  @click="currentPage = 1"
                  class="pagination-btn"
                />
                <v-btn
                  icon="mdi-chevron-left"
                  variant="text"
                  density="comfortable"
                  :disabled="currentPage === 1"
                  @click="currentPage--"
                  class="pagination-btn"
                />
                <span class="pagination-text mx-2">{{ currentPage }} / {{ totalPages }}</span>
                <v-btn
                  icon="mdi-chevron-right"
                  variant="text"
                  density="comfortable"
                  :disabled="currentPage === totalPages"
                  @click="currentPage++"
                  class="pagination-btn"
                />
                <v-btn
                  icon="mdi-chevron-double-right"
                  variant="text"
                  density="comfortable"
                  :disabled="currentPage === totalPages"
                  @click="currentPage = totalPages"
                  class="pagination-btn"
                />
              </div>
            </div>
          </div> -->
        </v-card-text>
      </v-card>

      <!-- 添加物料对话框 -->
      <v-dialog v-model="showAddDialog" max-width="850" max-height="600px" persistent>
        <v-card class="delete-confirm-dialog">
          <v-card-title class="d-flex justify-space-between align-center">
            <span>添加物料</span>
            <v-btn
              icon="mdi-close"
              variant="text"
              @click="showAddDialog = false,customMaterialName = '',customMaterialSpecDesc = '',customMaterialBigClass = '',customMaterialType = '',customMaterialPrice = null,customMaterialImageUrls = []"
            />
          </v-card-title>
          <v-card-text style="display: flex;gap: 10px;align-items: flex-start;">
            <!-- <div>
              <img 
                v-if="customMaterialImageUrls && customMaterialImageUrls.length > 0"
                :src="customMaterialImageUrls[0]?.url"
                style="width: 80px;"
                alt="物料图" 
                class="material-image"
              >
              <div v-else class="no-img" style="width: 80px;height: 80px;line-height: 80px;cursor: pointer;" @click.stop="handleNewImageUpload">
                <img src="@/assets/uploadIcon.png" alt="上传" class="img-icon-one" title="上传图片" />
              </div>
            </div> -->
            <v-textarea style="width: 200px;" auto-grow resize v-model="customMaterialName" rows="1" label="物料名称" persistent-placeholder variant="outlined" density="compact" hide-details @input="debouncedLoadMaterialNameList" ></v-textarea> 
            <v-textarea style="width: 200px;" auto-grow resize v-model="customMaterialSpecDesc" rows="1" label="规格" persistent-placeholder variant="outlined" density="compact" hide-details ></v-textarea>
            <v-textarea style="width: 200px;" auto-grow resize v-model="customMaterialRemark" rows="1" label="备注" persistent-placeholder variant="outlined" density="compact" hide-details ></v-textarea>
            <v-text-field style="width: 80px;" v-model="customMaterialUnit" label="单位" persistent-placeholder variant="outlined" density="compact" hide-details />
            <v-select
              label="大类"
              persistent-placeholder
              style="width: 100px;"
              v-model="customMaterialBigClass"
              :items="bigClassOptions"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
              @update:model-value="handleChangeCustomBigClass()"
            ></v-select>
            <v-select
              label="小类"
              persistent-placeholder
              style="width: 140px;"
              v-model="customMaterialType"
              :items="customMaterialBigClass ? getMaterialTypeOptions(customMaterialBigClass) : [{ title: '请先选大类', value: '', disabled: true }]"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="compact"
              hide-details
            ></v-select>
            <!-- <v-text-field style="width: 80px;" v-model="customMaterialPrice" label="单价" persistent-placeholder variant="outlined" density="compact" hide-details /> -->
          </v-card-text>
          <v-card-actions>
            <div class="d-flex correlate-btn">
              <v-btn color="error" variant="tonal" @click="showAddDialog = false">取消</v-btn>
              <v-btn color="primary" variant="flat" @click="handleAddMaterialToBank">确定</v-btn>
            </div>
          </v-card-actions>
          <v-card-text>
            <span v-if="materialNameList.length > 0" style="color: #fff;opacity: 1;font-size: 18px;">相似物料</span>
            <v-list class="material-name-list" style="background: #2B2D33;max-height: 300px;overflow-y: auto;">
              <v-list-item v-for="item in materialNameList" :key="item.id">
                <v-list-item-title style="-webkit-line-clamp:none;color: #fff;width: 210px;word-break: break-all;white-space: break-spaces;">{{ item.materialName }}</v-list-item-title>
                <v-list-item-subtitle style="-webkit-line-clamp:none;color: #fff;opacity: 1;width: 200px;word-break: break-all;white-space: break-spaces;">{{ item.specDesc || '/' }}</v-list-item-subtitle>
                <v-list-item-subtitle style="-webkit-line-clamp:none;color: #fff;opacity: 1;display: block;width: 210px;word-break: break-all;white-space: break-spaces;">{{ item.materialRemark || '/' }}</v-list-item-subtitle>
                <v-list-item-subtitle style="-webkit-line-clamp:none;color: #fff;opacity: 1;width: 80px;">{{ item.materialUnit || '/' }}</v-list-item-subtitle>
                <v-list-item-subtitle style="-webkit-line-clamp:none;color: #fff;opacity: 1;width: 90px;">{{ item.bigClass || '/' }}</v-list-item-subtitle>
                <v-list-item-subtitle style="-webkit-line-clamp:none;color: #fff;opacity: 1;width: 100px;">{{ item.materialType || '/' }}</v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-dialog>
      
      <!-- 上传对话框 -->
      <UploadImgDialog
        v-model="showUploadDialog"
        mode="new"
        type="image"
        @upload="handleNewImageUploadSuccess"
      />

      <!-- 导入物料excel -->
      <v-dialog v-model="importExcelDialogOpen" max-width="500">
        <ImportExcel
          accept=".xls,.xlsx"
          icon="mdi-file-document"
          :type="importExcelTypeKind"
          @uploadExcel="handleImportExcel"
          @importStart="handleImportStart"
          @close="importExcelDialogOpen = false"
        />
      </v-dialog>
      <!-- 公共Loading组件 -->
      <div v-if="showCommonLoading" class="common-loading-overlay">
        <div class="common-loading-container">
          <div class="common-loading-spinner"></div>
          <p class="common-loading-text">{{ commonLoadingText || '加载中...' }}</p>
        </div>
      </div>
      <!-- 删除确认对话框 -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card class="delete-dialog">
          <v-card-title class="text-h5">
            确认删除
          </v-card-title>
          <v-card-text>
            确定要删除物料 "{{ selectedMaterial?.materialName }}" 吗？
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="error"
              variant="tonal"
              @click="deleteDialog = false"
            >
              取消
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="confirmDelete"
              :loading="deleteLoading"
            >
              确认删除
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useSnackbar } from '@/composables/useSnackbar'
import { contractApi } from '@/services/api'
import { bigClassOptions, getMaterialTypeOptions, materialUnitOptions } from '@/constants/contract'
import { useUserStore } from '@/stores/user'
import UploadImgDialog from '@/components/UploadImgDialog.vue'
import ImportExcel from '@/components/ImportMaterialExcel.vue'

interface Material {
  id?: number
  materialName: string
  materialCode?: string
  specDesc?: string
  remark?: string
  materialRemark?: string
  materialUnit?: string
  bigClass?: string
  materialType?: string
  materialPrice?: number
  materialImageUrls?: string
}

interface SearchForm {
  keyword: string
}

const { showSuccess, showError } = useSnackbar()
const userStore = useUserStore()

// 表格列配置
const headers = [
  { title: '编码', key: 'materialCode', align: 'center', width: '100px' },
  { title: '物料名称', key: 'materialName', align: 'center', width: '200px' },
  { title: '规格', key: 'specDesc', align: 'center', width: '160px' },
  { title: '备注', key: 'remark', align: 'center', width: '200px' },
  { title: '单位', key: 'materialUnit', align: 'center', width: '100px' },
  { title: '大类', key: 'bigClass', align: 'center', width: '120px' },
  { title: '小类', key: 'materialType', align: 'center', width: '120px' },
  { title: '操作', key: 'action', align: 'center', width: '100px' }
]


// 响应式数据
const loading = ref(false)
const currentPage = ref(1)
const pageSize = ref(30)
const total = ref(0)
const materialList = ref<Material[]>([])
const materialNameList = ref<Material[]>([])
const searchForm = ref<SearchForm>({
  keyword: ''
})

// 计算属性
const totalPages = computed(() => Math.ceil(total.value / pageSize.value))

// 新增物料相关状态
const showAddDialog = ref(false)
const addForm = ref()
const addFormValid = ref(false)
const addLoading = ref(false)
const newMaterial = ref<Material>({
  materialName: '',
  specDesc: '',
  remark: '',
  materialUnit: '',
  bigClass: '',
  materialType: ''
})

// 自定义物料相关变量
const showUploadDialog = ref(false)
const customMaterialImageUrls = ref<{ url: string; name: string }[]>([])
const customMaterialName = ref('')
const customMaterialSpecDesc = ref('')
const customMaterialBigClass = ref('')
const customMaterialType = ref('')
const customMaterialPrice = ref<number | null>(null)
const customMaterialUnit = ref('')
const customMaterialRemark = ref('')
// 删除相关状态
const deleteDialog = ref(false)
const deleteLoading = ref(false)
const selectedMaterial = ref<Material | null>(null)
const selectedIndex = ref(-1)

const importExcelDialogOpen = ref(false)
const importExcelTypeKind = ref(1)
const showCommonLoading = ref(false)
const commonLoadingText = ref('')
// 防抖保存
let saveTimers: { [key: number]: number | null } = {}

const debouncedSave = (item: Material, index: number) => {
  // 清除该索引对应的旧定时器
  if (saveTimers[index]) {
    clearTimeout(saveTimers[index])
  }
  
  // 设置新的定时器
  saveTimers[index] = setTimeout(() => {
    handleSave(item, index)
    saveTimers[index] = null
  }, 1000) as unknown as number
}

// 防抖加载物料名称列表
let materialNameTimer: number | null = null

const debouncedLoadMaterialNameList = () => {
  // 清除旧的定时器
  if (materialNameTimer) {
    clearTimeout(materialNameTimer)
  }
  
  // 设置新的定时器
  materialNameTimer = setTimeout(() => {
    loadMaterialNameList(customMaterialName.value)
    materialNameTimer = null
  }, 1000) as unknown as number
}

// 加载名称类似的物料列表
const loadMaterialNameList = async (materialName: string) => {
  try {
    const res = await contractApi.getProjectTypeList({
      page: 1, 
      pageSize: 30,
      materialName: materialName
    })
    if (res?.code === 0) {
      materialNameList.value = res.data.records || []
    }
  } catch (error) {
    console.error('获取名称类似的物料列表失败:', error)
  }
}

// 加载物料列表
const loadMaterialList = async () => {
  try {
    loading.value = true
    const res = await contractApi.getProjectTypeList({ 
      page: currentPage.value, 
      pageSize: pageSize.value,
      keyword: searchForm.value.keyword
    })
    if (res?.code === 0) {
      materialList.value = res.data.records || []
    }
  } catch (error) {
    console.error('获取物料列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 保存物料
const handleSave = async (item: Material, index: number) => {
//   if (!item.id) {
    try {
      const res = await contractApi.addPresetMaterial({
        id: item.id,
        materialName: item.materialName,
        specDesc: item.specDesc,
        bigClass: item.bigClass,
        materialType: item.materialType,
        materialUnit: item.materialUnit,
        materialRemark: item.materialRemark
      })
      if (res.code === 0) {
        // 更新ID
        materialList.value[index].id = res.data.id
        // showSuccess('物料保存成功')
      } else {
        // showError(res.message || '保存失败')
      }
    } catch (error: any) {
      console.error('保存物料失败:', error)
      showError(error.message || '保存失败')
    }
//   }
}

// 导入物料
const importMaterial = () => {
  customMaterialName.value = ''
  customMaterialSpecDesc.value = ''
  customMaterialBigClass.value = ''
  customMaterialType.value = ''
  customMaterialPrice.value = null
  customMaterialImageUrls.value = []
  importExcelDialogOpen.value = true
}

// 下载物料模板
const downloadMaterial = () => {
  const url = 'https://avalon-1304177540.cos.ap-shanghai.myqcloud.com/client/jiangfeng/excel/%E7%89%A9%E6%96%99%E5%AF%BC%E5%85%A5%E6%A8%A1%E7%89%88.xlsx'
  const a = document.createElement('a')
  a.href = url
  a.download = '物料模板.xlsx'
  a.click()
}
// 处理导入开始
const handleImportStart = () => {
  importExcelDialogOpen.value = true
}

// 导入excel
const handleImportExcel = async (files: { url: string; name: string }[]) => {
  const excelUrl = files[0].url
  showCommonLoading.value = true
  commonLoadingText.value = '正在导入物料...'
  try {
    const res = await contractApi.importPresetMaterialExcel ({
      // id: Number(contractId.value),
      url: excelUrl,
      // row: row,
      // type: type,
      // sheetIndexList: sheetIndexList
    })
    if(res?.code === 0){
      // 隐藏导入框
      importExcelDialogOpen.value = false
      showCommonLoading.value = false
      commonLoadingText.value = ''
      showSuccess(res.data || '导入成功')
      loadMaterialList()
      // 开始检查导入状态
      // findImportProductStatus()
      
      // handleImportStart()
      // if(message){
      //   showSuccess(message) 
      // }else{
      //   showSuccess('文档上传成功') 
      // }
    }else if(res?.code === -1){
      showError(res.message)
      showCommonLoading.value = false
      commonLoadingText.value = ''
    }else{
      showError(res.message || '导入失败，请检查表格是否符合规范')
      showCommonLoading.value = false
      commonLoadingText.value = ''
    }
  } catch (err) {
    showError(err.message || '导入失败，请检查表格是否符合规范')
    showCommonLoading.value = false
    commonLoadingText.value = ''
  }
}

// 处理大类变化
const handleChangeBigClass = (item: Material, index: number) => {
  item.materialType = ''
  debouncedSave(item, index)
}

// 方法
const handleSearch = () => {
  currentPage.value = 1
  loadMaterialList()
}

const handleReset = () => {
  searchForm.value = {
    keyword: ''
  }
  handleSearch()
}

const handleAdd = () => {
  showAddDialog.value = true
  materialNameList.value = []
  customMaterialName.value = ''
  customMaterialSpecDesc.value = ''
  customMaterialBigClass.value = ''
  customMaterialType.value = ''
  customMaterialPrice.value = null
  customMaterialImageUrls.value = []
  customMaterialUnit.value = ''
  customMaterialRemark.value = ''
}

// 处理大类变化 - 自定义物料
const handleChangeCustomBigClass = () => {
  customMaterialType.value = ''
}

// 处理新增物料图上传
const handleNewImageUpload = () => {
  showUploadDialog.value = true
}

// 处理图片上传成功
const handleNewImageUploadSuccess = (urls: string[]) => {
  customMaterialImageUrls.value = urls.map(url => ({
    url,
    name: `物料图_${Date.now()}`
  }))
  showUploadDialog.value = false
}

// 添加物料到物料库
const handleAddMaterialToBank = async () => {
  if (customMaterialName.value == '' || customMaterialName.value == null) {
    showError('物料名称不能为空')
    return
  }
  if (customMaterialBigClass.value == '' || customMaterialBigClass.value == null) {
    showError('大类不能为空')
    return
  }
  if (customMaterialType.value == '' || customMaterialType.value == null) {
    showError('小类不能为空')
    return
  }
  if (customMaterialUnit.value == '' || customMaterialUnit.value == null) {
    showError('单位不能为空')
    return
  }
  if(customMaterialSpecDesc.value == '' || customMaterialSpecDesc.value == null) {
    showError('规格不能为空')
    return
  }
  try {
    const res = await contractApi.addPresetMaterial({
      materialName: customMaterialName.value,
      specDesc: customMaterialSpecDesc.value,
      bigClass: customMaterialBigClass.value,
      materialType: customMaterialType.value,
      materialRemark: customMaterialRemark.value,
    //   materialPrice: customMaterialPrice.value,
      materialUnit: customMaterialUnit.value,
      materialImageUrls: customMaterialImageUrls.value.length > 0 ? JSON.stringify(customMaterialImageUrls.value) : ''
    })
    if (res?.code === 0) {
      showSuccess('添加物料成功')
      showAddDialog.value = false
      loadMaterialList()
    } else {
      showError(res?.message || '添加失败')
    }
  } catch (error: any) {
    console.error('添加物料失败:', error)
    showError(error.message || error.msg || '添加失败')
  }
}

const handleDelete = (item: Material, index: number) => {
  if(userStore.userInfo?.auths.includes('jf_goods')){
    selectedMaterial.value = item
    selectedIndex.value = index
    deleteDialog.value = true
  }else{
    showError('无权限删除')
    return
  }
}

const confirmDelete = async () => {
//   if (!selectedMaterial.value?.id) {
//     // 如果没有ID，说明是新增未保存的物料，直接从列表中移除
//     materialList.value.splice(selectedIndex.value, 1)
//     deleteDialog.value = false
//     selectedMaterial.value = null
//     selectedIndex.value = -1
//     return
//   }
  
  try {
    deleteLoading.value = true
    const res = await contractApi.deletePresetMaterial({
      id: selectedMaterial.value.id
    })
    if (res.code === 0) {
      showSuccess('删除成功')
      materialList.value.splice(selectedIndex.value, 1)
    //   loadMaterialList()
    } else {
      showError(res.message || '删除失败')
    }
    deleteDialog.value = false
  } catch (error: any) {
    showError(error.message || '删除失败')
  } finally {
    deleteLoading.value = false
    selectedMaterial.value = null
    selectedIndex.value = -1
  }
}

// 监听分页变化
watch([currentPage, pageSize], () => {
  window.scrollTo(0, 0)
  loadMaterialList()
})

// 初始化
onMounted(() => {
  loadMaterialList()
})
</script>

<style scoped>
.search-input :deep(.mdi-magnify),
.search-input :deep(.mdi-magnify::before),
.search-input :deep(.mdi-close::before),
.search-input :deep(.mdi-close-circle),
.search-input :deep(.mdi-close) {
  color: #fff!important;
}

.material-library-page {
  min-height: 100vh;
  width: 100%;
  background-color: #2B2D33;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 16px;
}

.material-library-container {
  width: 100%;
  max-width: 1400px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.text-primary {
  color: #D2475D !important;
}

.search-card {
  background-color: #2B2D33 !important;
  border: none !important;
  position: relative;
  z-index: 20;
}

.search-card :deep(.v-text-field) .v-field__input,
.search-card :deep(.v-text-field) .v-field__field,
.search-card :deep(.v-text-field) input,
.search-card :deep(.v-text-field) .v-field__append-inner,
.search-card :deep(.v-select) .v-field__input,
.search-card :deep(.v-select) .v-field__field,
.search-card :deep(.v-select) input,
.search-card :deep(.v-select) .v-select__selection-text,
.search-card :deep(.v-select) .v-select__selection,
.search-card :deep(.v-select) .v-field__append-inner {
  color: #fff !important;
}

.search-card :deep(.v-text-field) .v-field__outline,
.search-card :deep(.v-select) .v-field__outline {
  color: rgba(255, 255, 255, 0.12) !important;
}

.search-card :deep(.v-text-field) .v-field__input::placeholder {
  color: rgba(255, 255, 255, 0.7) !important;
}

.search-card :deep(.v-text-field) .v-label,
.search-card :deep(.v-select) .v-label {
  color: rgba(255, 255, 255, 0.7) !important;
}

:deep(.v-card) {
  box-shadow: none !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.table-card {
  background-color: #2B2D33 !important;
  flex: 1;
  display: flex;
  position: relative;
  z-index: 1;
}

.table-card :deep(.v-card-text) {
  padding: 16px;
}

.material-table {
  background-color: #2B2D33;
}

.material-table :deep(th) {
  background-color: #2B2D33 !important;
  color: rgba(255, 255, 255, 0.7) !important;
  font-weight: 500;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
  padding: 12px 8px !important;
}

.material-table :deep(td) {
  color: #fff !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
  padding: 8px !important;
}

.material-table :deep(tr:hover) {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.material-table :deep(.v-text-field) .v-field__input,
.material-table :deep(.v-text-field) .v-field__field,
.material-table :deep(.v-text-field) input,
.material-table :deep(.v-textarea) .v-field__input,
.material-table :deep(.v-textarea) .v-field__field,
.material-table :deep(.v-textarea) textarea,
.material-table :deep(.v-select) .v-field__input,
.material-table :deep(.v-select) .v-field__field,
.material-table :deep(.v-select) input,
.material-table :deep(.v-select) .v-select__selection-text,
.material-table :deep(.v-select) .v-select__selection {
  color: #fff !important;
}

.material-table :deep(.v-text-field) .v-field__outline,
.material-table :deep(.v-textarea) .v-field__outline,
.material-table :deep(.v-select) .v-field__outline {
  color: rgba(255, 255, 255, 0.12) !important;
}

.custom-pagination {
  padding: 16px;
  color: rgba(255, 255, 255, 0.7);
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}

.custom-pagination .pagination-info {
  font-size: 14px;
}

.custom-pagination .pagination-controls {
  display: flex;
  align-items: center;
}

.custom-pagination .pagination-btn {
  color: rgba(255, 255, 255, 0.7) !important;
}

.custom-pagination .pagination-btn:hover {
  color: #fff !important;
}

.custom-pagination .pagination-btn:disabled {
  color: rgba(255, 255, 255, 0.3) !important;
}

.custom-pagination .pagination-text {
  font-size: 14px;
}

.search-input {
  width: 100%;
}

.gap-2 {
  gap: 8px;
}

.search-btn {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  font-size: 14px;
  padding: 0 16px;
  min-width: unset;
}

.delete-dialog {
  background-color: #2B2D33 !important;
}

.delete-dialog :deep(.v-card-title) {
  color: #fff !important;
}

.delete-dialog :deep(.v-card-text) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.delete-dialog :deep(.v-btn) {
  text-transform: none;
  letter-spacing: 0;
}

:deep(th),:deep(td) {
  padding: 8px !important;
}

.no-img {
  background-color: rgba(0, 0, 0, 0.5);
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  text-align: center;
  border-radius: 4px;
}

.img-icon-one {
  width: 32px;
  height: 32px;
  opacity: 0.5;
}

.material-image {
  display: block;
  margin: 0 auto;
  object-fit: cover;
}

.correlate-btn {
  width: 100%;
  justify-content: flex-end;
  gap: 10px;
  padding: 0 16px 16px;
}

.delete-confirm-dialog {
  background-color: #2B2D33 !important;
}

.delete-confirm-dialog :deep(.v-card-title) {
  color: #fff !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding: 16px;
}

.delete-confirm-dialog :deep(.v-card-text) {
  color: rgba(255, 255, 255, 0.7) !important;
  padding: 24px 16px;
}

.delete-confirm-dialog :deep(.v-text-field) .v-field__input,
.delete-confirm-dialog :deep(.v-text-field) .v-field__field,
.delete-confirm-dialog :deep(.v-text-field) input,
.delete-confirm-dialog :deep(.v-textarea) .v-field__input,
.delete-confirm-dialog :deep(.v-textarea) .v-field__field,
.delete-confirm-dialog :deep(.v-textarea) textarea,
.delete-confirm-dialog :deep(.v-select) .v-field__input,
.delete-confirm-dialog :deep(.v-select) .v-field__field,
.delete-confirm-dialog :deep(.v-select) input,
.delete-confirm-dialog :deep(.v-select) .v-select__selection-text,
.delete-confirm-dialog :deep(.v-select) .v-select__selection {
  color: #fff !important;
}

.delete-confirm-dialog :deep(.v-text-field) .v-field__outline,
.delete-confirm-dialog :deep(.v-textarea) .v-field__outline,
.delete-confirm-dialog :deep(.v-select) .v-field__outline {
  color: rgba(255, 255, 255, 0.12) !important;
}

.delete-confirm-dialog :deep(.v-text-field) .v-label,
.delete-confirm-dialog :deep(.v-textarea) .v-label,
.delete-confirm-dialog :deep(.v-select) .v-label {
  color: rgba(255, 255, 255, 0.7) !important;
}

.delete-confirm-dialog :deep(.v-btn) {
  text-transform: none;
  letter-spacing: 0;
}

:deep(.v-list-item__content) {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

:deep(.material-name-list)::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

:deep(.material-name-list)::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 3px;
}

:deep(.material-name-list)::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

:deep(.material-name-list)::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.4);
}

.common-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.common-loading-container {
  text-align: center;
  color: #fff;
}

.common-loading-spinner {
  border: 4px solid #2B2D33;
  border-top: 4px solid #D2475D;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.common-loading-text {
  margin-top: 16px;
  font-size: 14px;
}

:deep(.mdi:before, .mdi-set){
    color: #FFFFFF;
}

</style>

